// ── Novelty engine context ──────────────────────────────────
// Lets any node in the graph (or any other UI surface) read its
// novelty score, QD cell coordinates, and accept transformations
// without prop drilling.
//
// Set up at the App level once the engine results arrive via SSE.

import { createContext, useContext } from 'react';

export const NoveltyContext = createContext({
  scores: {},        // { [nodeId]: { score, closestExisting, whyNovel } }
  cells: {},         // { [nodeId]: { mechanism, risk, audience, horizon } }
  filters: {         // active filters for the tree
    minNovelty: 0,
    cellMechanism: null,
    cellRisk: null,
    cellAudience: null,
    cellHorizon: null,
  },
  setFilter: () => {},
  clearFilters: () => {},
  archive: null,     // full QD archive (cells + dimensions + stats)
  recombinations: [], // blends
  blendScores: {},
  dmadResults: [],
  // Live per-reasoner status — { [reasonerId]: { label, model, status: 'running'|'done'|'error', elapsedMs, timeoutMs, error? } }
  // Updated by _dmad_status SSE events every 15s while a reasoner is in flight.
  dmadStatus: {},
  // Action handlers
  onAcceptTransformation: null, // (transformation, source) => void
  onDrillNovelty: null,
  // Phase C: evolutionary loop
  evolveGenerations: {},   // { [genIdx]: [candidate, ...] }
  evolveLineage: [],       // [{ childId, parentIds, operator, genIdx }]
  evolveActiveGen: null,
  // Research grounding status (populated by `_researchStatus` SSE event).
  // Surfaces in the SCORES tab so users know whether the engine ran on
  // grounded research or training-data opinion.
  researchStatus: null,    // null | { succeededCount, totalCount, briefBytes, gemini }
  // Competition grounding (Tier 2: auto on top-N, Tier 3: on-demand).
  // See features/ai-workflows/novelty-engine-phase-a.feature.
  competition: {},         // { [candidateId]: { status, competitorCount, competitors[], gapStatement, citations[], confidence } }
  verifications: {},       // { [candidateId]: deep brief from /api/verify-novelty }
  onVerifyNovelty: null,   // (candidate, candidateId) => Promise<brief>
});

export function useNovelty() {
  return useContext(NoveltyContext);
}

// Color helpers for novelty score (kept here so node + panel use same palette)
export function noveltyColor(score) {
  if (score == null) return '#3a3a3a';
  if (score >= 70) return '#34d399'; // green
  if (score >= 40) return '#fbbf24'; // yellow
  return '#f87171'; // red
}

export function noveltyLabel(score) {
  if (score == null) return null;
  if (score >= 90) return 'Reframes';
  if (score >= 70) return 'Notable';
  if (score >= 50) return 'Variant';
  if (score >= 30) return 'Common';
  return 'Cliché';
}
